import React from 'react';
import PageLayout from '../components/PageLayout';
import TechStack from '../components/TechStack';
import SEOHead from '../components/SEOHead';

const processSteps = [
  { num: '1', title: 'Discover', desc: 'Projects are envisioned, designed and prioritized. We choose a methodology such as agile, scrum or kanban that helps to guide the work process for the project.' },
  { num: '2', title: 'Plan', desc: 'Break the big ideas down into manageable chunks across our team with user stories, issues and tasks.' },
  { num: '3', title: 'Building and testing', desc: 'Our development teams work to build production-ready software that meets requirements and feedback. CI/CD pipelines ensure an efficient developer experience.' },
  { num: '4', title: 'Deploy', desc: "With code written, tested and merged, it's time to ship it." },
  { num: '5', title: 'Operate', desc: 'Support and maintenance are required for active software projects.' },
];

const features = [
  { icon: '/pictures/shield_check_7246d46e1b.svg', title: 'Safety', desc: 'We prioritize customer safety. To minimize user risks, we design and develop software with user safety in mind, including the implementation of appropriate warning systems, error-handling mechanisms, and user education and training materials.' },
  { icon: '/pictures/software_f1fba72847.svg', title: 'Requirement adaptability', desc: 'We adapt to changing project requirements, technological advancements, and emerging industry trends. The ability to pivot quickly and adjust to changing circumstances is essential for success.' },
  { icon: '/pictures/maintenance_3c1849ccac.svg', title: 'Customization', desc: 'We aim to develop high-quality software that meets the needs of our customers. When creating software products, we prioritize user experience, design, and functionality.' },
  { icon: '/pictures/improved_quality_841c5f0827.svg', title: 'Continuous improvement', desc: 'We recognize that change is inevitable in any software development project. Hence, we are always ready for it.' },
];

export default function SoftwareProductDev() {
  return (
    <>
      <style>{`
        @media (max-width: 768px) {
          .service-content-grid,
          .features-grid,
          .process-steps-grid {
            grid-template-columns: 1fr !important;
            gap: 24px !important;
          }
          .service-section {
            padding: 60px 24px !important;
          }
        }
      `}</style>
      
      <PageLayout>
      <SEOHead
        title="Software Product Development"
        description="DISION Tech offers enterprise software product development using Agile methodologies. Security-first, custom software solutions for businesses of all sizes."
        keywords="software product development, custom software, agile development, scrum, security software, enterprise software"
        url="/development/software-product-development"
      />
      {/* Page Hero */}
      <div style={{ background: '#F9FAFB', padding: '60px 24px 50px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <span style={{ fontSize: '14px', color: '#6B7280' }}>Development</span>
            <span style={{ color: '#D1D5DB' }}>/</span>
            <span style={{ fontSize: '14px', color: '#0099CC', fontWeight: '500' }}>Software Product Development</span>
          </div>
          <h1 style={{ fontSize: 'clamp(32px, 4vw, 52px)', fontWeight: '800', color: '#111827', marginBottom: '16px', letterSpacing: '-1px', lineHeight: 1.15 }}>
            Software Product Development
          </h1>
          <p style={{ fontSize: '18px', color: '#6B7280', maxWidth: '640px', lineHeight: 1.7 }}>
            DISION Tech offers Software product development services for large enterprises to small ones, that meet today's fast-paced business environment.
          </p>
        </div>
      </div>

      {/* What is section */}
      <section className="service-section" style={{ padding: '80px 24px', background: 'white' }}>
        <div className="service-content-grid" style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '80px', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontSize: '36px', fontWeight: '700', color: '#111827', marginBottom: '24px', letterSpacing: '-0.5px' }}>What is Software Product Development?</h2>
            <p style={{ fontSize: '16px', color: '#6B7280', lineHeight: 1.8, marginBottom: '20px' }}>
              Software product development is a repetitive logical process that aims to build a programmed software product to mark a unique personal or business goal, process or objective. Basically, it's a process is creating a comprehensive vision for the product or service.
            </p>
            <p style={{ fontSize: '16px', color: '#6B7280', lineHeight: 1.8 }}>
              Agile software development is a methodology that has gained immense popularity in recent years. In DISION Tech, we use Agile software development, especially Scrum and Kanban which is a more responsive and adaptable approach that focuses on customer satisfaction and delivering value.
            </p>
          </div>
          <div style={{ borderRadius: '20px', overflow: 'hidden', lineHeight: 0 }}>
            <img src="/pictures/intro_bg1_2a870dcff5.png" alt="Software Development" style={{ width: '100%', height: 'auto', display: 'block' }} onError={e => { e.target.src = 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=600&q=80'; }} />
          </div>
        </div>
      </section>

      {/* Continuous improvement */}
      <section className="service-section" style={{ padding: '80px 24px', background: '#F9FAFB' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '36px', fontWeight: '700', color: '#111827', marginBottom: '12px', letterSpacing: '-0.5px' }}>Continuous improvement</h2>
          <p style={{ fontSize: '17px', color: '#6B7280', marginBottom: '56px', maxWidth: '500px' }}>Our core principles that drive every project we build.</p>
          <div className="features-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '32px' }}>
            {features.map((f, i) => (
              <div key={i} className="service-card" style={{ background: 'white', borderRadius: '16px', padding: '32px', border: '1px solid #E5E7EB', display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
                <div style={{ width: '52px', height: '52px', flexShrink: 0, borderRadius: '12px', background: '#F0F9FF', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px' }}>
                  <img src={f.icon} alt={f.title} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                </div>
                <div>
                  <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#111827', marginBottom: '8px' }}>{f.title}</h3>
                  <p style={{ fontSize: '15px', color: '#6B7280', lineHeight: 1.7, margin: 0 }}>{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section style={{ padding: '80px 24px', background: 'white' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '36px', fontWeight: '700', color: '#111827', marginBottom: '12px', letterSpacing: '-0.5px' }}>Software Product Development Process</h2>
          <p style={{ fontSize: '17px', color: '#6B7280', marginBottom: '56px', maxWidth: '560px', lineHeight: 1.7 }}>A solid development process and methodology ensure that our software product is developed efficiently and effectively.</p>
          <div style={{ position: 'relative' }}>
            {processSteps.map((step, i) => (
              <div key={i} style={{ display: 'flex', gap: '24px', marginBottom: i < processSteps.length - 1 ? '32px' : '0', position: 'relative' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                  <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: '#0099CC', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ color: 'white', fontWeight: '700', fontSize: '16px' }}>{step.num}</span>
                  </div>
                  {i < processSteps.length - 1 && <div style={{ width: '2px', flex: 1, background: '#E5E7EB', marginTop: '8px', minHeight: '32px' }} />}
                </div>
                <div style={{ paddingBottom: '8px', paddingTop: '10px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#111827', marginBottom: '8px' }}>{step.title}</h3>
                  <p style={{ fontSize: '15px', color: '#6B7280', lineHeight: 1.7, margin: 0 }}>{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security & Feedback cards */}
      <section style={{ padding: '0 24px 80px', background: 'white' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
          <div style={{ background: '#F0F9FF', borderRadius: '16px', padding: '32px', border: '1px solid #BAE6FD', display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
            <img src="/pictures/security_6b532b3e59.svg" alt="Security" style={{ width: '40px', height: '40px', flexShrink: 0 }} />
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#111827', marginBottom: '8px' }}>Security first</h3>
              <p style={{ fontSize: '15px', color: '#6B7280', lineHeight: 1.7, margin: 0 }}>The software handling today's data is under assault. Protecting information is paramount, as it's at the epicentre of every business process and relationship.</p>
            </div>
          </div>
          <div style={{ background: '#FFF7ED', borderRadius: '16px', padding: '32px', border: '1px solid #FED7AA', display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
            <img src="/pictures/feedback_d8c5f11718.svg" alt="Feedback" style={{ width: '40px', height: '40px', flexShrink: 0 }} />
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#111827', marginBottom: '8px' }}>Continuous feedback</h3>
              <p style={{ fontSize: '15px', color: '#6B7280', lineHeight: 1.7, margin: 0 }}>By gathering continuous feedback internally, DISION Tech can improve our processes and increase value to customers.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Tech stack */}
      <section style={{ padding: '0 24px 80px', background: 'white' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '32px', fontWeight: '700', color: '#111827', marginBottom: '40px', letterSpacing: '-0.5px' }}>Our tech stack</h2>
          <TechStack />
        </div>
      </section>
    </PageLayout>
    </>
  );
}
